import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Popover, Button, Divider, Stack } from "@mui/material";
import { intl, DmsSelect } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";
import { DmsAnalyticsPeriodPicker } from "./DmsAnalyticsPeriodPicker";
import { CHANNEL_OPTIONS } from "./constants";

export const DmsAnalyticsFilters = ({
    showChannel = true,
    showPeriod = true,
    showApplyButton = false,
    children,
    sx,
    ...props
}) => {
    const {
        filters,
        filtersAnchorEl,
        closeFilters,
        updateFilter,
        clearAllFilters,
        applyFilters,
        isFiltered,
    } = useDmsAnalyticsContext();

    const handleApply = () => {
        applyFilters();
        closeFilters();
    };

    const handleClear = () => {
        clearAllFilters();
        closeFilters();
    };

    const channelOptions = useMemo(
        () => [
            { value: CHANNEL_OPTIONS.ALL, label: intl.get("DmsAnalytics.Channel.All").d("Todos") },
            { value: CHANNEL_OPTIONS.PUBLIC, label: intl.get("DmsAnalytics.Channel.Public").d("Público") },
            { value: CHANNEL_OPTIONS.PRIVATE, label: intl.get("DmsAnalytics.Channel.Private").d("Privado") },
        ],
        []
    );

    return (
        <Popover
            open={Boolean(filtersAnchorEl)}
            anchorEl={filtersAnchorEl}
            onClose={closeFilters}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { p: 2, minWidth: 280, maxWidth: 320, ...sx } }}
            {...props}
        >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                {intl.get("DmsAnalytics.Filter.Title").d("Filtros")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {showChannel && (
                    <>
                        <DmsSelect
                            label={intl.get("DmsAnalytics.Filter.Channel").d("Canal")}
                            value={filters.channel}
                            onChange={(value) => updateFilter("channel", value)}
                            options={channelOptions}
                            size="small"
                        />
                        <Divider />
                    </>
                )}

                {showPeriod && (
                    <DmsAnalyticsPeriodPicker showMonthSelects showYearSelect />
                )}

                {children}
            </Box>

            {showApplyButton ? (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="primary" onClick={handleApply} fullWidth size="small">
                            {intl.get("Apply").d("Aplicar")}
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleClear} fullWidth size="small">
                            {intl.get("Clear").d("Limpar")}
                        </Button>
                    </Stack>
                </>
            ) : (
                isFiltered && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="text"
                                color="error"
                                onClick={clearAllFilters}
                                size="small"
                            >
                                {intl.get("DmsAnalytics.Action.ClearFilters").d("Limpar Filtros")}
                            </Button>
                        </Box>
                    </>
                )
            )}
        </Popover>
    );
};

DmsAnalyticsFilters.propTypes = {
    showChannel: PropTypes.bool,
    showPeriod: PropTypes.bool,
    showApplyButton: PropTypes.bool,
    children: PropTypes.node,
    sx: PropTypes.object,
};
